(() => {
  if (window.PDVG && typeof window.PDVG.init === 'function') {
    window.PDVG.init(document);
    return;
  }

  const instances = new Map();

  class DecorativeVideoGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-gallery-track]');
      this.items = Array.from(root.querySelectorAll('[data-gallery-item]'));
      this.previewVideos = Array.from(root.querySelectorAll('[data-preview-video]'));
      this.modal = root.querySelector('[data-gallery-modal]');
      this.dialog = root.querySelector('[data-gallery-dialog]');
      this.modalVideo = root.querySelector('[data-modal-video]');
      this.closeButtons = Array.from(root.querySelectorAll('[data-close-modal]'));
      this.prevButton = root.querySelector('[data-prev]');
      this.nextButton = root.querySelector('[data-next]');
      this.soundButton = root.querySelector('[data-toggle-sound]');
      this.soundLabel = root.querySelector('[data-sound-label]');
      this.currentIndex = 0;
      this.timer = null;
      this.resumeTimer = null;
      this.scrollRaf = null;
      this.isModalOpen = false;
      this.soundEnabled = false;
      this.isSectionInView = false; // KDK-PERF V2: unknown is NOT visible.
      this.destroyed = false;
      this.visibilityRaf = null;
      this.loadedVideos = new WeakSet();
      this.designMode = !!window.Shopify?.designMode;
      this.reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      this.autoscrollMs = Math.max(
        2000,
        (parseInt(root.dataset.autoscrollSeconds, 10) || 4) * 1000
      );
      this.pauseOnHover = root.dataset.pauseOnHover !== 'false';

      this.boundKeydown = this.onKeydown.bind(this);
      this.boundTrackScroll = this.onTrackScroll.bind(this);
      this.boundUserInteract = this.onUserInteract.bind(this);
      this.boundVisibilityChange = this.onVisibilityChange.bind(this);
      this.boundBlockSelect = this.onBlockSelect.bind(this);
      this.boundBlockDeselect = this.onBlockDeselect.bind(this);
      this.boundMouseEnter = this.onMouseEnter.bind(this);
      this.boundMouseLeave = this.onMouseLeave.bind(this);
      this.boundViewportCheck = this.scheduleViewportCheck.bind(this);

      if (!this.track || !this.items.length) {
        return;
      }

      this.setupObservers();
      this.bindEvents();
      // The observer (or measured fallback) decides which previews can load.
      this.updateNavigation();

      if (!this.designMode && !this.reduceMotion) {
        this.startAutoscroll();
      }
    }

    setupObservers() {
      if ('IntersectionObserver' in window) {
        this.sectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.root || this.destroyed) return;
            this.isSectionInView = entry.isIntersecting;
            this.refreshPreviewState();
          });
        }, { threshold: 0, rootMargin: '0px' });
        this.sectionObserver.observe(this.root);
        // Root = viewport, so an off-screen track never preloads its children.
        this.previewObserver = new IntersectionObserver(() => {
          this.playVisiblePreviews();
        }, { root: null, threshold: [0, 0.25, 0.5, 1], rootMargin: '0px' });
        this.previewVideos.forEach((video) => this.previewObserver.observe(video));
      } else {
        window.addEventListener('scroll', this.boundViewportCheck, { passive: true });
        window.addEventListener('resize', this.boundViewportCheck, { passive: true });
        this.scheduleViewportCheck();
      }
    }

    scheduleViewportCheck() {
      if (this.visibilityRaf || this.destroyed) return;
      this.visibilityRaf = requestAnimationFrame(() => {
        this.visibilityRaf = null;
        const r = this.root.getBoundingClientRect();
        this.isSectionInView = r.width > 0 && r.height > 0 && r.bottom > 0 &&
          r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
        this.refreshPreviewState();
      });
    }

    refreshPreviewState() {
      if (this.destroyed) return;
      if (!this.isSectionInView || this.isModalOpen || document.hidden) {
        this.pauseAllPreviews();
        this.stopAutoscroll();
        return;
      }
      this.playVisiblePreviews();
      if (!this.timer) this.startAutoscroll();
    }

    isPreviewVisible(video) {
      if (!video || !video.isConnected) return false;
      const r = video.getBoundingClientRect();
      const t = this.track.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0 || t.width <= 0 || t.height <= 0) return false;
      const width = Math.max(0, Math.min(r.right, t.right, window.innerWidth) - Math.max(r.left, t.left, 0));
      const height = Math.max(0, Math.min(r.bottom, t.bottom, window.innerHeight) - Math.max(r.top, t.top, 0));
      return width * height / (r.width * r.height) >= 0.25;
    }

    bindEvents() {
      this.items.forEach((item, index) => {
        const opener = item.querySelector('[data-open-modal]');

        opener?.addEventListener('click', () => {
          this.open(index);
        });
      });

      this.prevButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next());
      this.soundButton?.addEventListener('click', () => this.toggleSound());

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.track.addEventListener('scroll', this.boundTrackScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.boundUserInteract, { passive: true });
      this.track.addEventListener('touchstart', this.boundUserInteract, { passive: true });
      this.track.addEventListener('wheel', this.boundUserInteract, { passive: true });
      this.track.addEventListener('focusin', this.boundUserInteract);

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.boundMouseEnter);
        this.root.addEventListener('mouseleave', this.boundMouseLeave);
      }

      document.addEventListener('visibilitychange', this.boundVisibilityChange);
      document.addEventListener('shopify:block:select', this.boundBlockSelect);
      document.addEventListener('shopify:block:deselect', this.boundBlockDeselect);
    }

    hydrateVideo(video) {
      if (!video || this.destroyed || this.loadedVideos.has(video) ||
          !this.isSectionInView || this.isModalOpen || document.hidden ||
          this.reduceMotion || !this.isPreviewVisible(video)) return;

      const src = video.dataset.src;
      if (!src) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      // One fallback only: a missing optional preview must not break the gallery.
      video.addEventListener('error', () => {
        const fallback = video.dataset.fallbackSrc;
        if (!fallback || video.dataset.kdkFallbackUsed === 'true' || this.destroyed) return;
        video.dataset.kdkFallbackUsed = 'true';
        video.dataset.src = fallback;
        video.removeAttribute('src');
        this.loadedVideos.delete(video);
        this.playVisiblePreviews();
      });
      video.src = src;
      this.loadedVideos.add(video);
      video.load();
    }

    ensureAdjacentVideosLoaded(index) {
      // Keep the old call sites, but do not hydrate hidden neighbours.
      this.playVisiblePreviews();
    }

    playVisiblePreviews() {
      const allowed = !this.destroyed && this.isSectionInView && !this.isModalOpen &&
        !document.hidden && !this.reduceMotion;
      this.previewVideos.forEach((video) => {
        if (allowed && this.isPreviewVisible(video)) {
          this.hydrateVideo(video);
          if (this.loadedVideos.has(video) && video.paused) {
            const result = video.play();
            if (result && result.catch) result.catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    }

    pauseAllPreviews() {
      this.previewVideos.forEach((video) => video.pause());
    }

    startAutoscroll() {
      if (this.destroyed || document.hidden || this.items.length <= 1 || this.isModalOpen || !this.isSectionInView || this.designMode || this.reduceMotion) {
        return;
      }

      this.stopAutoscroll();

      this.timer = window.setInterval(() => {
        this.stepAutoscroll();
      }, this.autoscrollMs);
    }

    stopAutoscroll() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    scheduleAutoscrollRestart() {
      if (this.designMode || this.reduceMotion || this.isModalOpen || !this.isSectionInView) return;

      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        this.startAutoscroll();
      }, this.autoscrollMs);
    }

    onUserInteract() {
      this.stopAutoscroll();
      this.scheduleAutoscrollRestart();
    }

    onMouseEnter() {
      this.stopAutoscroll();
    }

    onMouseLeave() {
      this.startAutoscroll();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoscroll();
        this.pauseAllPreviews();
        return;
      }

      if (!this.isModalOpen) {
        this.playVisiblePreviews();
        this.startAutoscroll();
      }
    }

    onTrackScroll() {
      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
      }

      this.scrollRaf = requestAnimationFrame(() => {
        const nearestIndex = this.getNearestIndex();

        this.playVisiblePreviews();
        if (nearestIndex !== this.currentIndex) {
          this.currentIndex = nearestIndex;
          this.ensureAdjacentVideosLoaded(this.currentIndex);
          this.updateNavigation();
        }
      });
    }

    getNearestIndex() {
      if (!this.items.length) return 0;

      const trackRect = this.track.getBoundingClientRect();
      const targetCenter = trackRect.left + trackRect.width / 2;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const rect = item.getBoundingClientRect();
        const itemCenter = rect.left + rect.width / 2;
        const distance = Math.abs(targetCenter - itemCenter);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    scrollToIndex(index, behavior = 'smooth') {
      const target = this.items[index];
      if (!target) return;

      const paddingLeft = parseFloat(window.getComputedStyle(this.track).paddingLeft) || 0;
      const left = target.offsetLeft - paddingLeft;

      this.track.scrollTo({
        left,
        behavior: this.reduceMotion ? 'auto' : behavior
      });

      this.currentIndex = index;
      this.ensureAdjacentVideosLoaded(index);
      this.updateNavigation();
    }

    stepAutoscroll() {
      if (!this.items.length) return;

      const nextIndex = this.currentIndex >= this.items.length - 1 ? 0 : this.currentIndex + 1;
      this.scrollToIndex(nextIndex);
    }

    open(index) {
      if (!this.modal || !this.modalVideo) return;

      this.currentIndex = Math.max(0, Math.min(index, this.items.length - 1));
      this.isModalOpen = true;
      this.stopAutoscroll();
      window.clearTimeout(this.resumeTimer);
      this.pauseAllPreviews();
      this.soundEnabled = false;
      this.syncModalVideo();

      this.modal.hidden = false;
      requestAnimationFrame(() => {
        this.modal.classList.add('is-visible');
      });

      document.documentElement.classList.add('pdvg-lock');
      document.body.classList.add('pdvg-lock');
      document.addEventListener('keydown', this.boundKeydown);
    }

    close() {
      if (!this.modal || !this.modalVideo || !this.isModalOpen) return;

      this.isModalOpen = false;
      this.modal.classList.remove('is-visible');
      this.modal.hidden = true;
      this.modalVideo.pause();
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load();
      this.modalVideo.dataset.currentSrc = '';
      this.soundEnabled = false;
      this.updateSoundButton();

      document.documentElement.classList.remove('pdvg-lock');
      document.body.classList.remove('pdvg-lock');
      document.removeEventListener('keydown', this.boundKeydown);

      this.playVisiblePreviews();
      this.startAutoscroll();
    }

    previous() {
      if (this.currentIndex <= 0) return;
      this.currentIndex -= 1;
      this.syncModalVideo();
    }

    next() {
      if (this.currentIndex >= this.items.length - 1) return;
      this.currentIndex += 1;
      this.syncModalVideo();
    }

    syncModalVideo() {
      const item = this.items[this.currentIndex];
      if (!item || !this.modalVideo) return;

      const src = item.dataset.videoSrc;
      const poster = item.dataset.poster || '';
      const label = item.dataset.label || `Vídeo ${this.currentIndex + 1}`;

      if (src && this.modalVideo.dataset.currentSrc !== src) {
        this.modalVideo.pause();
        this.modalVideo.src = src;
        this.modalVideo.dataset.currentSrc = src;
        this.modalVideo.load();
      }

      if (poster) {
        this.modalVideo.poster = poster;
      } else {
        this.modalVideo.removeAttribute('poster');
      }

      this.modalVideo.setAttribute('aria-label', label);
      this.modalVideo.muted = !this.soundEnabled;
      this.modalVideo.defaultMuted = !this.soundEnabled;
      this.updateSoundButton();
      this.scrollToIndex(this.currentIndex);
      this.modalVideo.play().catch(() => {});
    }

    toggleSound() {
      this.soundEnabled = !this.soundEnabled;
      if (this.modalVideo) {
        this.modalVideo.muted = !this.soundEnabled;
      }
      this.updateSoundButton();
    }

    updateSoundButton() {
      if (!this.soundButton) return;

      this.soundButton.setAttribute('aria-pressed', this.soundEnabled ? 'true' : 'false');
      this.soundButton.setAttribute(
        'aria-label',
        this.soundEnabled ? 'Silenciar vídeo' : 'Activar sonido del vídeo'
      );

      if (this.soundLabel) {
        this.soundLabel.textContent = this.soundEnabled ? 'Silenciar vídeo' : 'Activar sonido del vídeo';
      }
    }

    updateNavigation() {
      if (!this.prevButton || !this.nextButton) return;

      const hasMultiple = this.items.length > 1;

      this.prevButton.hidden = !hasMultiple;
      this.nextButton.hidden = !hasMultiple;

      if (!hasMultiple) return;

      this.prevButton.disabled = this.currentIndex === 0;
      this.nextButton.disabled = this.currentIndex === this.items.length - 1;
    }

    onKeydown(event) {
      if (!this.isModalOpen) return;

      if (event.key === 'Escape') {
        this.close();
      }

      if (event.key === 'ArrowLeft') {
        this.previous();
      }

      if (event.key === 'ArrowRight') {
        this.next();
      }
    }

    onBlockSelect(event) {
      const target = event.target;
      if (!this.root.contains(target)) return;

      this.stopAutoscroll();

      const index = this.items.findIndex((item) => item === target || item.contains(target));
      if (index > -1) {
        this.scrollToIndex(index, 'smooth');
      } else {
        this.root.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    onBlockDeselect(event) {
      const target = event.target;
      if (!this.root.contains(target)) return;

      this.startAutoscroll();
    }

    destroy() {
      this.destroyed = true;
      if (this.visibilityRaf) cancelAnimationFrame(this.visibilityRaf);
      window.removeEventListener('scroll', this.boundViewportCheck);
      window.removeEventListener('resize', this.boundViewportCheck);
      this.stopAutoscroll();
      window.clearTimeout(this.resumeTimer);

      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
      }

      this.pauseAllPreviews();
      this.isModalOpen = false;

      if (this.modal) {
        this.modal.classList.remove('is-visible');
        this.modal.hidden = true;
      }

      if (this.modalVideo) {
        this.modalVideo.pause();
        this.modalVideo.removeAttribute('src');
        this.modalVideo.load();
      }

      document.documentElement.classList.remove('pdvg-lock');
      document.body.classList.remove('pdvg-lock');

      this.track?.removeEventListener('scroll', this.boundTrackScroll);
      this.track?.removeEventListener('pointerdown', this.boundUserInteract);
      this.track?.removeEventListener('touchstart', this.boundUserInteract);
      this.track?.removeEventListener('wheel', this.boundUserInteract);
      this.track?.removeEventListener('focusin', this.boundUserInteract);
      this.root?.removeEventListener('mouseenter', this.boundMouseEnter);
      this.root?.removeEventListener('mouseleave', this.boundMouseLeave);

      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      document.removeEventListener('keydown', this.boundKeydown);
      document.removeEventListener('shopify:block:select', this.boundBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.boundBlockDeselect);

      this.sectionObserver?.disconnect();
      this.previewObserver?.disconnect();
    }
  }

  const getRoots = (scope) => {
    if (!scope) return [];

    if (scope.matches?.('[data-decorative-video-gallery]')) {
      return [scope];
    }

    return Array.from(scope.querySelectorAll?.('[data-decorative-video-gallery]') || []);
  };

  const init = (scope = document) => {
    const roots = getRoots(scope);

    roots.forEach((root) => {
      const key = root.id || root.dataset.galleryId;
      if (!key) return;

      const existing = instances.get(key);
      if (existing) {
        existing.destroy();
      }

      const instance = new DecorativeVideoGallery(root);
      instances.set(key, instance);
    });
  };

  const destroy = (scope = document) => {
    const roots = getRoots(scope);

    roots.forEach((root) => {
      const key = root.id || root.dataset.galleryId;
      if (!key) return;

      const instance = instances.get(key);
      if (!instance) return;

      instance.destroy();
      instances.delete(key);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroy(event.target);
  });

  window.PDVG = {
    init,
    destroy,
    instances
  };
})();
